/**
 * Flow 1 + 2 + 3 — onboarding: URL in, Blueprint out.
 *
 * `POST /projects` -> `POST /projects/{id}/ingest` -> poll `GET /jobs/{id}` ->
 * `POST /projects/{id}/blueprints` -> poll -> open the review screen. Every step is a
 * real contract call; nothing is faked locally, so the same component works against the
 * live API in Wave 2.
 */
import { useEffect, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useCreateProject, useGenerateBlueprint, useIngestSite, useJob } from '@/api/hooks'
import type { GenerateRequest, IngestRequest, ProjectSummary } from '@/api/types'
import { formatNumber } from '@/lib/format'
import { REFERENCE_PUSH_OPERATIONS } from '@/lib/ops'
import { isMockEnabled } from '@/mocks/mode'
import { demoProjectId } from '@/mocks/backend'
import { Badge, Button, Cap, Checkbox, ErrorNotice, Field, Input, Panel, Spinner } from '@/components/ui'


interface FormState {
  siteUrl: string
  name: string
  services: string
  locations: string
  budget: string
  exclusions: string
  tone: string
  languages: string
  competitors: string
}

const EXAMPLE: FormState = {
  siteUrl: 'https://rapidplumbingmanchester.co.uk',
  name: 'Rapid Plumbing Manchester',
  services: 'Emergency plumbing, Boiler repair, Boiler servicing, Bathroom fitting',
  locations: 'Manchester, Salford',
  budget: '900',
  exclusions: 'free, cheap, jobs, training',
  tone: 'straight-talking, local, no-nonsense',
  languages: 'en',
  competitors: '',
}

const EMPTY: FormState = {
  siteUrl: '',
  name: '',
  services: '',
  locations: '',
  budget: '',
  exclusions: '',
  tone: '',
  languages: 'en',
  competitors: '',
}

function list(value: string): string[] {
  return value
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part !== '')
}

function hostToName(url: string): string {
  try {
    const host = new URL(url).hostname.replace(/^www\./, '')
    return host
      .split('.')[0]!
      .split(/[-_]/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  } catch {
    return ''
  }
}

function urlProblem(value: string): string | null {
  if (value.trim() === '') return 'A site URL is required.'
  try {
    const parsed = new URL(value)
    if (!parsed.protocol.startsWith('http')) return 'Use an http(s) URL.'
    if (value.length > 2083) return 'URL is longer than the contract allows (2083 characters).'
    return null
  } catch {
    return 'That is not a valid absolute URL.'
  }
}

type Phase = 'idle' | 'ingesting' | 'generating' | 'done'

export function IngestScreen() {
  const navigate = useNavigate()
  const [form, setForm] = useState<FormState>(EMPTY)
  const [project, setProject] = useState<ProjectSummary | null>(null)
  // Phase and job id move together, in one state object: advancing the phase in a
  // separate update would let the effect observe "generating" while still holding the
  // *finished* ingest job, and it would walk straight past the generate step.
  const [run, setRun] = useState<{ phase: Phase; jobId: string | null }>({
    phase: 'idle',
    jobId: null,
  })
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [includeLandingPages, setIncludeLandingPages] = useState(true)
  const [includeAdCopy, setIncludeAdCopy] = useState(true)
  const [failure, setFailure] = useState<unknown>(null)

  const createProject = useCreateProject()
  const ingest = useIngestSite()
  const generate = useGenerateBlueprint()
  const job = useJob(run.jobId)

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }))

  const urlError = form.siteUrl === '' ? null : urlProblem(form.siteUrl)
  const name = form.name.trim() !== '' ? form.name.trim() : hostToName(form.siteUrl)

  async function startGenerate(projectId: string) {
    const body: GenerateRequest = {
      include_ad_copy: includeAdCopy,
      include_landing_pages: includeLandingPages,
      notes: form.tone.trim() === '' ? null : `Tone: ${form.tone.trim()}`,
    }
    setRun({ phase: 'generating', jobId: null })
    const accepted = await generate.mutateAsync({ projectId, body })
    setRun({ phase: 'generating', jobId: accepted.job_id })
  }

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault()
    setFailure(null)
    const problem = urlProblem(form.siteUrl)
    if (problem) {
      setFailure(new Error(problem))
      return
    }
    try {
      const created = await createProject.mutateAsync({
        name,
        site_url: form.siteUrl.trim(),
        services: list(form.services),
        locations: list(form.locations),
        exclusions: list(form.exclusions),
        languages: list(form.languages),
        monthly_budget_micros: form.budget.trim() === '' ? null : Math.round(Number(form.budget) * 1_000_000),
      })
      setProject(created)
      setRun({ phase: 'ingesting', jobId: null })
      const body: IngestRequest = {
        site_url: form.siteUrl.trim(),
        services: list(form.services),
        locations: list(form.locations),
        exclusions: list(form.exclusions),
        languages: list(form.languages),
        tone: form.tone.trim() === '' ? null : form.tone.trim(),
        competitor_urls: list(form.competitors),
        monthly_budget_micros: form.budget.trim() === '' ? null : Math.round(Number(form.budget) * 1_000_000),
        force_recrawl: false,
      }
      const accepted = await ingest.mutateAsync({ projectId: created.id, body })
      setRun({ phase: 'ingesting', jobId: accepted.job_id })
    } catch (error) {
      setFailure(error)
      setRun({ phase: 'idle', jobId: null })
    }
  }

  // Job completion drives the next step: ingest -> generate -> project screen.
  const jobStatus = job.data?.status
  useEffect(() => {
    if (run.jobId === null || jobStatus !== 'succeeded') return
    if (run.phase === 'ingesting' && project) {
      void startGenerate(project.id)
    } else if (run.phase === 'generating' && project) {
      setRun({ phase: 'done', jobId: null })
      void navigate({ to: '/projects/$projectId', params: { projectId: project.id } })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run, jobStatus, project])

  const phase = run.phase
  const busy = phase === 'ingesting' || phase === 'generating'

  return (
    <div className="flex flex-col gap-6">
      <section className="panel-raised px-6 py-7">
        <Cap>Flow 1 — ingest</Cap>
        <h1 className="mt-2 max-w-3xl text-[26px] leading-9 font-semibold tracking-tight text-mist-100">
          Give AdForge a URL and a budget. Get a complete, reviewable search account — paused, never
          live by surprise.
        </h1>
        <p className="mt-2 max-w-2xl text-[13.5px] text-mist-400">
          AdForge crawls the site, clusters keyword ideas into ad group themes, writes policy-clean
          RSAs and a landing page per ad group. You review the diff, edit in place, dry-run against
          the platform, then push. Campaigns are always created paused.
        </p>
      </section>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
        <Panel title="New project" subtitle="Only the URL is required; everything else sharpens the plan.">
          <form className="flex flex-col gap-4" onSubmit={onSubmit}>
            <Field label="Site URL" htmlFor="site-url" error={urlError} hint="The site AdForge crawls for offerings, geography and tone.">
              <Input
                id="site-url"
                data-testid="site-url"
                value={form.siteUrl}
                placeholder="https://example.co.uk"
                onChange={(event) => update('siteUrl', event.target.value)}
                autoComplete="url"
                required
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Project name" htmlFor="project-name" hint="Defaults to the domain.">
                <Input
                  id="project-name"
                  data-testid="project-name"
                  value={form.name}
                  placeholder={hostToName(form.siteUrl) || 'Rapid Plumbing Manchester'}
                  onChange={(event) => update('name', event.target.value)}
                />
              </Field>
              <Field label="Monthly budget" htmlFor="budget" hint="Used to size budgets and ad group count.">
                <Input
                  id="budget"
                  data-testid="budget"
                  value={form.budget}
                  inputMode="decimal"
                  placeholder="900"
                  onChange={(event) => update('budget', event.target.value)}
                />
              </Field>
            </div>

            <Field label="Services" htmlFor="services" hint="Comma-separated. Leave blank to let the crawl decide.">
              <Input
                id="services"
                data-testid="services"
                value={form.services}
                placeholder="Emergency plumbing, Boiler repair"
                onChange={(event) => update('services', event.target.value)}
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Locations" htmlFor="locations" hint="Geo targeting, comma-separated.">
                <Input
                  id="locations"
                  data-testid="locations"
                  value={form.locations}
                  placeholder="Manchester, Salford"
                  onChange={(event) => update('locations', event.target.value)}
                />
              </Field>
              <Field label="Exclusions" htmlFor="exclusions" hint="Never target or bid on these.">
                <Input
                  id="exclusions"
                  data-testid="exclusions"
                  value={form.exclusions}
                  placeholder="free, cheap, jobs"
                  onChange={(event) => update('exclusions', event.target.value)}
                />
              </Field>
            </div>

            <button
              type="button"
              className="w-fit text-[12px] text-mist-400 underline decoration-dotted hover:text-mist-200"
              onClick={() => setShowAdvanced((prev) => !prev)}
              aria-expanded={showAdvanced}
            >
              {showAdvanced ? 'Hide' : 'Show'} advanced inputs
            </button>

            {showAdvanced ? (
              <div className="grid gap-4 rounded-lg border border-ink-750 bg-ink-850/60 p-3.5 sm:grid-cols-2">
                <Field label="Tone" htmlFor="tone">
                  <Input
                    id="tone"
                    value={form.tone}
                    placeholder="straight-talking, local"
                    onChange={(event) => update('tone', event.target.value)}
                  />
                </Field>
                <Field label="Languages" htmlFor="languages">
                  <Input
                    id="languages"
                    value={form.languages}
                    onChange={(event) => update('languages', event.target.value)}
                  />
                </Field>
                <Field label="Competitor URLs" htmlFor="competitors" hint="Optional. Competitor terms get their own ad group.">
                  <Input
                    id="competitors"
                    value={form.competitors}
                    onChange={(event) => update('competitors', event.target.value)}
                  />
                </Field>
                <div className="flex flex-col gap-2">
                  <Checkbox
                    label="Write ad copy (RSA headlines and descriptions)"
                    checked={includeAdCopy}
                    onChange={setIncludeAdCopy}
                    testId="include-ad-copy"
                  />
                  <Checkbox
                    label="Render one landing page per ad group"
                    checked={includeLandingPages}
                    onChange={setIncludeLandingPages}
                    testId="include-landing-pages"
                  />
                </div>
              </div>
            ) : null}

            {failure ? <ErrorNotice error={failure} /> : null}

            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="submit"
                variant="primary"
                data-testid="start-ingest"
                disabled={busy || createProject.isPending}
              >
                {busy ? 'Working…' : 'Ingest site and build the Blueprint'}
              </Button>
              <Button
                variant="ghost"
                data-testid="use-example"
                onClick={() => setForm(EXAMPLE)}
                disabled={busy}
              >
                Use an example
              </Button>
              {isMockEnabled() ? (
                <Button
                  variant="secondary"
                  data-testid="open-demo-project"
                  disabled={busy}
                  onClick={() => navigate({ to: '/projects/$projectId', params: { projectId: demoProjectId() } })}
                >
                  Open the demo project
                </Button>
              ) : null}
            </div>
          </form>
        </Panel>

        <div className="flex flex-col gap-5">
          <Panel title="Pipeline" subtitle="Each step is a queued job; nothing runs on the request path.">
            <ol className="flex flex-col gap-3" data-testid="ingest-pipeline">
              {(
                [
                  ['ingest', 'Crawl the site', 'Offerings, geography, tone, brand assets.'],
                  ['research', 'Keyword ideas', 'Volume and CPC per idea, cached per customer id.'],
                  ['generate', 'Structure and copy', 'Clustering, themes, negatives, RSAs, landing pages.'],
                ] as Array<[string, string, string]>
              ).map(([key, title, body]) => {
                const active =
                  (key === 'ingest' && phase === 'ingesting') ||
                  (key === 'generate' && phase === 'generating')
                const done = key === 'ingest' ? phase !== 'idle' && phase !== 'ingesting' : phase === 'done'
                return (
                  <li key={key} className="flex gap-3">
                    <span
                      aria-hidden="true"
                      className={`mt-[6px] size-2.5 shrink-0 rounded-full border ${
                        done ? 'border-signal-ok bg-signal-ok' : active ? 'border-forge-400 bg-forge-400 pulse-dot' : 'border-ink-600 bg-ink-900'
                      }`}
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-[13px] text-mist-100">{title}</span>
                        {active ? <Badge tone="forge">running</Badge> : null}
                        {done ? <Badge tone="ok">done</Badge> : null}
                      </div>
                      <p className="text-[12px] text-mist-400">{body}</p>
                    </div>
                  </li>
                )
              })}
            </ol>

            {run.jobId ? (
              <div className="mt-4 rounded-lg border border-ink-750 bg-ink-850 px-3 py-2.5" data-testid="job-progress">
                <div className="flex items-center justify-between gap-3">
                  <Cap>Job {job.data?.kind ?? '…'}</Cap>
                  <span className="num text-[11.5px] text-mist-400">
                    {job.data ? `${Math.round(job.data.progress * 100)}%` : 'queued'}
                  </span>
                </div>
                <div className="relative mt-1.5 h-1.5 overflow-hidden rounded-full bg-ink-750">
                  <div
                    className="h-full rounded-full bg-forge-400 transition-[width] duration-300"
                    style={{ width: `${Math.round((job.data?.progress ?? 0) * 100)}%` }}
                  />
                </div>
                <p className="mt-1.5 text-[12px] text-mist-400">{job.data?.message ?? 'Waiting for a worker…'}</p>
              </div>
            ) : null}

            {job.isError ? <div className="mt-3"><ErrorNotice error={job.error} /></div> : null}
            {generate.isError ? <div className="mt-3"><ErrorNotice error={generate.error} /></div> : null}
          </Panel>

          <Panel title="What you get" subtitle="The Blueprint is the product; the push is a downstream action on it.">
            <ul className="flex flex-col gap-2 text-[12.5px] text-mist-300">
              <li className="flex gap-2">
                <span className="text-forge-400">→</span> One campaign structure with themed ad groups,
                keyword mix and negatives.
              </li>
              <li className="flex gap-2">
                <span className="text-forge-400">→</span> RSAs inside platform character limits, every
                line labelled with the evidence it came from.
              </li>
              <li className="flex gap-2">
                <span className="text-forge-400">→</span> One landing page per ad group, published
                <em> before</em> any campaign points at it.
              </li>
              <li className="flex gap-2">
                <span className="text-forge-400">→</span> A versioned document: edit creates v n+1, and the
                diff between versions is the review screen.
              </li>
              <li className="flex gap-2">
                <span className="text-forge-400">→</span> An ops-cost estimate before you commit.{' '}
                <span className="num">{formatNumber(REFERENCE_PUSH_OPERATIONS)}</span> operations is
                one realistic campaign push — on Explorer access the whole platform affords about seven
                of those per day.
              </li>
            </ul>
          </Panel>
        </div>
      </div>

      {phase === 'done' ? <Spinner label="Opening the review screen…" /> : null}
      {createProject.isError ? <ErrorNotice error={createProject.error} /> : null}
      {ingest.isError ? <ErrorNotice error={ingest.error} /> : null}
    </div>
  )
}
